import React from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchTypeOfMeetings } from "../services/typeOfMeetingService";
import { Spinner, Alert, Row, Col } from "react-bootstrap";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import Tag from "../components/Tag";
import { getTypeOfMeetingPath } from "../utils/contentRoutes";

import {
  StyledButton,
  StyledTitle,
  StyledContainer,
  StyledCard,
  StyledCardImg,
  StyledSubTitle,
} from "../components/StyledComponents";

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: auto;
`;

const TypeOfMeetingPage = () => {
  const navigate = useNavigate();

  const {
    data: typeOfMeetings = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["typeOfMeetings"],
    queryFn: fetchTypeOfMeetings,
    staleTime: 300000,
  });

  const filteredMeetings = typeOfMeetings.filter(
    (meeting) => meeting.name !== "Indywidualne spotkanie | wolne miejsce"
  );

  if (isLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p>Loading meeting types...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <Alert variant="danger" className="text-center mt-5">
        Failed to load meeting types.
      </Alert>
    );
  }

  return (
    <StyledContainer>
      <StyledTitle className="text-center mb-4">Nasza Oferta</StyledTitle>
      <Row>
        {filteredMeetings.length > 0 ? (
          filteredMeetings.map((meeting) => (
            <Col key={meeting.id} md={4} className="mb-4">
              <StyledCard
                key={meeting.id}
                $height="600px"
                role="link"
                tabIndex={0}
                onClick={() => navigate(getTypeOfMeetingPath(meeting))}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    navigate(getTypeOfMeetingPath(meeting));
                  }
                }}
                style={{ cursor: "pointer" }}
                aria-label={`Zobacz szczegóły: ${meeting.name}`}
              >
                <div style={{ position: "relative" }}>
                  {/* 🔖 Tags absolutely positioned in top-left */}
                  <div
                    style={{
                      position: "absolute",
                      top: "0.5rem",
                      left: "2rem",
                      zIndex: 2,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "0.25rem",
                    }}
                  >
                    {meeting.isIndividual && <Tag type="individual" />}
                    {meeting.isSolo && <Tag type="solo" />}
                  </div>

                  {/* 🖼️ Image */}
                  <StyledCardImg
                    src={`${process.env.REACT_APP_API_URL}/${meeting.imageUrl}`}
                    alt={meeting.name}
                    className="meeting-img"
                    style={{
                      height: "300px",
                      objectFit: "cover",
                      objectPosition: "top",
                    }}
                  />
                </div>
                <StyledCard.Body className="meeting-details">
                  <StyledSubTitle className="meeting-title">
                    {meeting.name}
                  </StyledSubTitle>

                  <p
                    style={{
                      fontSize: "1rem",
                      fontWeight: "100",
                    }}
                  >
                    {meeting.shortDescription}
                  </p>
                  <ButtonRow>
                    <StyledButton
                      variant="primary"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate(getTypeOfMeetingPath(meeting));
                      }}
                    >
                      Zobacz szczegóły
                    </StyledButton>
                  </ButtonRow>
                </StyledCard.Body>
              </StyledCard>
            </Col>
          ))
        ) : (
          <Alert variant="info">No meeting types available.</Alert>
        )}
      </Row>
    </StyledContainer>
  );
};

export default TypeOfMeetingPage;
